import { useCallback } from 'react';
import { useAppDispatch } from '@/store/store';
import { openUpgradeModal } from '@/store/slices/subscriptionSlice';
import type { UpgradePromptContext } from '@/types/subscription';

/**
 * Dispatches `openUpgradeModal(context)` against the Redux store.
 *
 * Built for `LockedFeature` and `UpgradeCtaButton`
 * (`src/components/subscription/`, D51f) so no component hand-rolls its own
 * dispatch call — today's five hand-rolled sites (`VotingModeSelector.tsx`,
 * `CreateListModal.tsx`, `CreateCollaborativeListModal.tsx`,
 * `MemberManagementSection.tsx`, `TasteInsightsSnippet.tsx`) each do this
 * inline and converge onto this one hook.
 *
 * This calls `useDispatch` unconditionally, so a Redux `<Provider>` MUST be
 * mounted above any component that uses it (the app root provides one).
 * Passing `onUpgradeClick` to `LockedFeature` replaces the dispatch but does
 * NOT remove the hook call — the hook still runs, so the Provider is still
 * required. Unit tests satisfy it by mocking `react-redux` directly rather
 * than mounting a real store, matching this codebase's convention (see
 * `src/pages/dashboard/components/__tests__/reducedMotion.test.tsx`).
 */
export function useOpenUpgradeModal(): (context: UpgradePromptContext) => void {
  const dispatch = useAppDispatch();
  return useCallback(
    (context: UpgradePromptContext) => {
      dispatch(openUpgradeModal(context));
    },
    [dispatch]
  );
}
